import React, { useEffect, useState } from "react";
import { SectionCard } from "../../../components/panel/PanelUI";
import SignaturePad from "../../../components/SignaturePad";
import { getProfileSignature } from "../../../utils/profileSignature";

function createProfileForm(profile) {
  return {
    name: profile?.name || "",
    signature: getProfileSignature(profile),
    personalInfo: {
      firstName: profile?.personalInfo?.firstName || "",
      lastName: profile?.personalInfo?.lastName || "",
      fatherName: profile?.personalInfo?.fatherName || "",
      bookletNumber:
        profile?.personalInfo?.bookletNumber ||
        profile?.personalInfo?.idNumber ||
        "",
      nationalCode: profile?.personalInfo?.nationalCode || "",
      placeOfIssue: profile?.personalInfo?.placeOfIssue || "",
      birthDate: profile?.personalInfo?.birthDate || "",
      mobile: profile?.personalInfo?.mobile || "",
      phone: profile?.personalInfo?.phone || "",
      address: profile?.personalInfo?.address || "",
    },
    company: {
      isExists: Boolean(profile?.company?.isExists),
      name: profile?.company?.name || "",
      address: profile?.company?.address || "",
      phone: profile?.company?.phone || "",
    },
  };
}

// این کامپوننت فقط دیتا می‌گیرد و آن را نمایش می‌دهد
// هیچ فچی از API درون این فایل انجام نمی‌شود
export default function ProfileView({ profile, onSave, saving, saveError }) {
  const [form, setForm] = useState(() => createProfileForm(profile));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setForm(createProfileForm(profile));
  }, [profile]);

  const handleChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleCompanyChange = (key) => (e) => {
    const value = key === "isExists" ? e.target.checked : e.target.value;
    setForm((prev) => ({
      ...prev,
      company: {
        ...prev.company,
        [key]: value,
      },
    }));
  };

  const handlePersonalInfoChange = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [key]: e.target.value,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
    setEditing(false);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* هدر پروفایل */}
      <SectionCard>
        <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:text-right">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-navy text-2xl font-extrabold text-white">
            {(profile?.name || "؟").charAt(0)}
          </div>
          <div className="flex-1">
            <p className="text-base font-extrabold text-navy">
              {profile?.name || "کاربر"}
            </p>
            <p className="mt-0.5 text-xs text-gray-400" dir="ltr">
              {profile?.phone || "-"}
            </p>
          </div>
          <span className="rounded-full bg-sky/10 px-3 py-1 text-[11px] font-bold text-sky-dark">
            سقف قولنامه: {profile?.agreement_limit ?? 0}
          </span>
        </div>
      </SectionCard>

      {/* فرم اطلاعات */}
      <SectionCard
        title="اطلاعات حساب کاربری"
        action={
          !editing && (
            <button
              onClick={() => setEditing(true)}
              className="rounded-lg bg-sky/10 px-3 py-1.5 text-xs font-bold text-sky-dark transition hover:bg-sky/20"
            >
              ویرایش
            </button>
          )
        }
      >
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <Field
            label="نام و نام خانوادگی"
            value={form.name}
            disabled={!editing}
            onChange={handleChange("name")}
          />
          <Field
            label="شماره موبایل"
            value={profile?.phone || ""}
            disabled
            dir="ltr"
          />
          <Field
            label="سقف ثبت قولنامه"
            value={profile?.agreement_limit ?? 0}
            disabled
            dir="ltr"
          />

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs font-bold text-gray-600">
              امضای پیش‌فرض
            </label>
            {editing ? (
              <SignaturePad
                value={form.signature}
                onChange={(signature) =>
                  setForm((prev) => ({ ...prev, signature: signature || "" }))
                }
              />
            ) : form.signature ? (
              <div className="flex min-h-28 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 p-3">
                <img
                  src={form.signature}
                  alt="امضای پیش‌فرض"
                  className="max-h-24 max-w-full object-contain"
                />
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-xs font-bold text-gray-400">
                امضایی ثبت نشده است
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <h3 className="mb-2 text-sm font-extrabold text-navy">
              اطلاعات هویتی
            </h3>
          </div>

          <Field
            label="نام"
            value={form.personalInfo.firstName}
            disabled={!editing}
            onChange={handlePersonalInfoChange("firstName")}
          />
          <Field
            label="نام خانوادگی"
            value={form.personalInfo.lastName}
            disabled={!editing}
            onChange={handlePersonalInfoChange("lastName")}
          />
          <Field
            label="نام پدر"
            value={form.personalInfo.fatherName}
            disabled={!editing}
            onChange={handlePersonalInfoChange("fatherName")}
          />
          <Field
            label="شماره شناسنامه"
            value={form.personalInfo.bookletNumber}
            disabled={!editing}
            onChange={handlePersonalInfoChange("bookletNumber")}
          />
          <Field
            label="کد ملی"
            value={form.personalInfo.nationalCode}
            disabled={!editing}
            onChange={handlePersonalInfoChange("nationalCode")}
          />
          <Field
            label="محل صدور شناسنامه"
            value={form.personalInfo.placeOfIssue}
            disabled={!editing}
            onChange={handlePersonalInfoChange("placeOfIssue")}
          />
          <Field
            label="تاریخ تولد"
            value={form.personalInfo.birthDate}
            disabled={!editing}
            onChange={handlePersonalInfoChange("birthDate")}
          />
          <Field
            label="تلفن همراه"
            value={form.personalInfo.mobile}
            disabled={!editing}
            onChange={handlePersonalInfoChange("mobile")}
          />
          <Field
            label="تلفن ثابت"
            value={form.personalInfo.phone}
            disabled={!editing}
            onChange={handlePersonalInfoChange("phone")}
          />
          <Field
            label="آدرس"
            value={form.personalInfo.address}
            disabled={!editing}
            full
            onChange={handlePersonalInfoChange("address")}
          />

          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm font-bold text-navy">
              <input
                type="checkbox"
                checked={form.company.isExists}
                disabled={!editing}
                onChange={handleCompanyChange("isExists")}
                className="h-4 w-4 accent-sky"
              />
              اطلاعات شرکت فعال است
            </label>
          </div>

          <Field
            label="نام شرکت"
            value={form.company.name}
            disabled={!editing || !form.company.isExists}
            onChange={handleCompanyChange("name")}
          />
          <Field
            label="تلفن شرکت"
            value={form.company.phone}
            disabled={!editing || !form.company.isExists}
            dir="ltr"
            onChange={handleCompanyChange("phone")}
          />
          <Field
            label="آدرس شرکت"
            value={form.company.address}
            disabled={!editing || !form.company.isExists}
            full
            onChange={handleCompanyChange("address")}
          />

          {saveError && (
            <div className="sm:col-span-2 rounded-xl bg-red-50 px-4 py-2.5 text-xs font-bold text-red-600 border border-red-100">
              {saveError}
            </div>
          )}

          {editing && (
            <div className="flex gap-2 sm:col-span-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-navy px-5 py-2.5 text-sm font-bold text-white transition hover:bg-navy-light disabled:opacity-60"
              >
                {saving ? "در حال ذخیره..." : "ذخیره تغییرات"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setForm(createProfileForm(profile));
                  setEditing(false);
                }}
                className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-bold text-navy transition hover:border-sky-light"
              >
                انصراف
              </button>
            </div>
          )}
        </form>
      </SectionCard>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  disabled,
  dir = "rtl",
  full = false,
}) {
  return (
    <div className={`flex flex-col gap-1 ${full ? "sm:col-span-2" : ""}`}>
      <label className="text-xs font-bold text-gray-600">{label}</label>
      <input
        type="text"
        dir={dir}
        value={value}
        disabled={disabled}
        onChange={onChange}
        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800 outline-none transition focus:border-sky focus:bg-white focus:ring-2 focus:ring-sky/20 disabled:text-gray-400 disabled:bg-gray-50"
      />
    </div>
  );
}
